import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAppDispatch, useAppSelector } from '@/store'

import {
  agentSettingsDraftWriteFor,
  agentSettingsFingerprint,
  dryRunAgentSettings,
  publishAgentSettings,
  selectAgentSettingsOperation,
  selectAgentSettingsOperationError,
  selectCanPublish,
  selectDryRunResult,
  selectSelectedDraft,
} from '../slices/agentSettingsSlice'
import type { AgentSettingsKey, ReviewPolicySettings } from '../types'
import { useAgentSettingsLabels } from '../agentSettingsLabels'

export interface AgentDryRunPanelProps {
  settingsKey: AgentSettingsKey
  hasUnsavedEdits: boolean
}

/** The server owns evaluation and publication; this component only gates their exact candidate. */
export function AgentDryRunPanel({
  settingsKey,
  hasUnsavedEdits,
}: AgentDryRunPanelProps) {
  const { t } = useTranslation('agents')
  const labels = useAgentSettingsLabels()
  const dispatch = useAppDispatch()
  const draft = useAppSelector((state) =>
    selectSelectedDraft(state, settingsKey)
  )
  const dryRun = useAppSelector((state) =>
    selectDryRunResult(state, settingsKey)
  )
  const operation = useAppSelector((state) =>
    selectAgentSettingsOperation(state, settingsKey)
  )
  const operationError = useAppSelector((state) =>
    selectAgentSettingsOperationError(state, settingsKey)
  )
  const input = useMemo(
    () => (draft === null ? null : agentSettingsDraftWriteFor(draft)),
    [draft]
  )
  const fingerprint = useMemo(
    () => (input === null ? '' : agentSettingsFingerprint(input)),
    [input]
  )
  const clientReasonValid =
    input === null ||
    input.scope !== 'CLIENT' ||
    (input.clientOverrideReason?.trim().length ?? 0) > 0
  const canPublish = useAppSelector((state) =>
    input === null || hasUnsavedEdits || !clientReasonValid
      ? false
      : selectCanPublish(state, settingsKey, fingerprint)
  )
  const error =
    operationError?.operation === 'dryRun' ||
    operationError?.operation === 'publish'
      ? operationError
      : null
  const alertRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (error !== null) alertRef.current?.focus()
  }, [error])

  const dryRunDisabled =
    input === null ||
    hasUnsavedEdits ||
    !clientReasonValid ||
    operation !== 'idle'
  const disabledReason =
    input === null
      ? t('guardrails.dryRun.disabled.noDraft')
      : hasUnsavedEdits
        ? t('guardrails.dryRun.disabled.unsaved')
        : !clientReasonValid
          ? t('guardrails.dryRun.disabled.clientReason')
          : null
  const publishDisabledReason = canPublish
    ? null
    : input === null
      ? t('guardrails.publish.disabled.noDraft')
      : hasUnsavedEdits
        ? t('guardrails.publish.disabled.unsaved')
        : !clientReasonValid
          ? t('guardrails.publish.disabled.clientReason')
          : operation !== 'idle'
            ? t('guardrails.publish.disabled.pending')
            : dryRun?.noChange === true
              ? t('guardrails.publish.disabled.noChange')
              : error !== null
                ? t('guardrails.publish.disabled.refused')
                : t('guardrails.publish.disabled.dryRun')
  const resolvedRows =
    dryRun === null
      ? []
      : ([
          [
            'autonomyRung',
            t('guardrails.dryRun.resolvedFields.autonomy'),
            dryRun.resolved.autonomyRung,
          ],
          [
            'costCeilingPerRunMinor',
            t('guardrails.dryRun.resolvedFields.costPerRun'),
            dryRun.resolved.costCeilingPerRunMinor,
          ],
          [
            'costCeilingPerPhaseMinor',
            t('guardrails.dryRun.resolvedFields.costPerPhase'),
            dryRun.resolved.costCeilingPerPhaseMinor,
          ],
          [
            'currency',
            t('guardrails.dryRun.resolvedFields.currency'),
            dryRun.resolved.currency,
          ],
          [
            'policyPresetKey',
            t('guardrails.dryRun.resolvedFields.policyPreset'),
            dryRun.resolved.policyPresetKey,
          ],
          [
            'reviewPolicy',
            t('guardrails.dryRun.resolvedFields.reviewPolicy'),
            dryRun.resolved.reviewPolicy,
          ],
          [
            'clientOverrideReason',
            t('guardrails.dryRun.resolvedFields.clientOverrideReason'),
            dryRun.resolved.clientOverrideReason,
          ],
          [
            'outputPermissions',
            t('guardrails.dryRun.resolvedFields.outputPermissions'),
            dryRun.resolved.outputPermissions,
          ],
        ] as const)
  /** The machine value, kept on data-value for audit and tests only. */
  const rawResolvedValue = (value: unknown): string => {
    if (value === undefined || value === null)
      return t('guardrails.states.empty')
    if (Array.isArray(value))
      return value.length === 0
        ? t('guardrails.states.empty')
        : value.join(', ')
    return typeof value === 'object' ? JSON.stringify(value) : String(value)
  }
  /** What a person reads: every server identifier mapped through i18n. */
  const displayResolvedValue = (field: string, value: unknown): string => {
    if (value === undefined || value === null)
      return t('guardrails.states.empty')
    if (field === 'autonomyRung') return labels.rung(String(value))
    if (field === 'reviewPolicy')
      return labels.reviewPolicy(value as ReviewPolicySettings)
    if (field === 'outputPermissions' && Array.isArray(value))
      return value.length === 0
        ? t('guardrails.states.empty')
        : value.map((item) => labels.outputPermission(String(item))).join(', ')
    return rawResolvedValue(value)
  }

  return (
    <section
      className="mt-6 rounded-lg border border-border bg-card p-4"
      data-testid="agent-dry-run-panel"
    >
      <SectionLabel>{t('guardrails.dryRun.title')}</SectionLabel>
      <p className="mt-2 text-sm text-muted-foreground">
        {t('guardrails.dryRun.description')}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          disabled={dryRunDisabled}
          onClick={() => {
            if (input !== null)
              void dispatch(
                dryRunAgentSettings({
                  key: settingsKey,
                  input,
                  fingerprint,
                })
              )
          }}
        >
          {t('guardrails.dryRun.action')}
        </Button>
        <Button
          type="button"
          variant="accent"
          disabled={!canPublish}
          aria-describedby={
            canPublish ? undefined : 'agent-publish-disabled-reason'
          }
          onClick={() => {
            void dispatch(
              publishAgentSettings({ key: settingsKey, fingerprint })
            )
          }}
        >
          {t('guardrails.publish.action')}
        </Button>
        {disabledReason !== null && (
          <p role="status" className="text-sm text-muted-foreground">
            {disabledReason}
          </p>
        )}
        {publishDisabledReason !== null && (
          <p
            id="agent-publish-disabled-reason"
            role="status"
            className="text-sm text-muted-foreground"
          >
            {publishDisabledReason}
          </p>
        )}
      </div>
      {error !== null && (
        <p
          ref={alertRef}
          tabIndex={-1}
          role="alert"
          className="mt-3 text-sm text-destructive"
          data-code={error.code}
          data-status={error.status}
        >
          {labels.serverMessage(error)}
        </p>
      )}
      {dryRun !== null && (
        <div className="mt-5 space-y-4" data-testid="agent-dry-run-result">
          <dl className="grid gap-2 text-sm sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(0,1fr)]">
            <div>
              <dt className="text-muted-foreground">
                {t('guardrails.dryRun.identity')}
              </dt>
              <dd
                className="font-mono text-xs"
                data-testid="agent-dry-run-identity"
              >
                {labels.scope(input?.scope)} ·{' '}
                {input === null ? '' : t(`phases.${input.phase}`)} ·{' '}
                {input?.agentKey}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t('guardrails.dryRun.digest')}
              </dt>
              <dd
                className="truncate font-mono text-xs"
                title={dryRun.candidateDigest}
                data-testid="agent-dry-run-digest"
              >
                {dryRun.candidateDigest}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t('guardrails.dryRun.noChange')}
              </dt>
              <dd>
                {dryRun.noChange
                  ? t('guardrails.states.yes')
                  : t('guardrails.states.no')}
              </dd>
            </div>
          </dl>
          <dl
            className="grid gap-3 text-sm sm:grid-cols-2"
            data-testid="agent-dry-run-resolved-values"
          >
            {resolvedRows.map(([field, label, rawValue]) => {
              const value = rawResolvedValue(rawValue)
              const shown = displayResolvedValue(field, rawValue)
              const rawProvenance = dryRun.resolved.provenance[field]
              const provenance = rawProvenance ?? t('guardrails.states.empty')
              return (
                <div key={field}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd
                    className="mt-1 break-words"
                    data-field={field}
                    data-value={value}
                    data-provenance={provenance}
                  >
                    {shown} ·{' '}
                    {rawProvenance === undefined
                      ? provenance
                      : labels.provenance(rawProvenance)}
                  </dd>
                </div>
              )
            })}
          </dl>
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('guardrails.dryRun.field')}</TableHead>
                  <TableHead>{t('guardrails.dryRun.current')}</TableHead>
                  <TableHead>{t('guardrails.dryRun.candidate')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dryRun.diff.map((row) => (
                  <TableRow
                    key={row.field}
                    data-testid={`agent-dry-run-diff-${row.field}`}
                  >
                    <TableCell>{labels.diffField(row.field)}</TableCell>
                    <TableCell className="whitespace-normal break-words">
                      {labels.diffValue(row.field, row.currentValue) ??
                        t('guardrails.states.empty')}
                    </TableCell>
                    <TableCell className="whitespace-normal break-words">
                      {labels.diffValue(row.field, row.candidateValue) ??
                        t('guardrails.states.empty')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </section>
  )
}
